/**
 * A que radiologos atiende este transcriptor.
 *
 * POR QUE EXISTE ESTA PANTALLA
 * ============================
 * En una IPS el transcriptor no es un recurso comun: cada especialista suele
 * tener el suyo. Su cola de trabajo sale de esta asignacion, y sin ella no ve
 * nada, a proposito: la alternativa —que sin asignar vea todo— convierte un
 * olvido en acceso a la historia clinica de la institucion entera.
 *
 * Un centro que sí trabaja con bolsa comun no necesita otra cosa: se le
 * marcan todos los radiologos. Mismo mecanismo, dos formas de organizarse.
 */

import { useState, useEffect } from 'react'
import { Loader2, Users, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/presentation/components/ui/Button'
import { useUsers } from '@/application/users/useUsers'
import {
  useRadiologosDelTranscriptor,
  useAsignarRadiologos,
} from '@/application/transcriptor-radiologos'
import { toast } from '@/lib/toast'
import { getErrorMessage } from '@/lib/api'

interface Props {
  /** El transcriptor al que se le asignan radiologos. */
  userId: string
  className?: string
}

export function RadiologosDelTranscriptor({ userId, className }: Props) {
  const { data: usuarios, isLoading: cargandoUsuarios } = useUsers({
    role: 'RADIOLOGIST',
    isActive: true,
    limit: 100,
  })
  const { data: asignados, isLoading: cargandoAsignados } = useRadiologosDelTranscriptor(userId)
  const asignar = useAsignarRadiologos(userId)

  const [seleccion, setSeleccion] = useState<string[]>([])

  useEffect(() => {
    if (asignados) setSeleccion(asignados)
  }, [asignados])

  const radiologos = usuarios?.users ?? []
  const cargando = cargandoUsuarios || cargandoAsignados
  const haCambiado =
    JSON.stringify([...seleccion].sort()) !== JSON.stringify([...(asignados ?? [])].sort())

  const alternar = (id: string) =>
    setSeleccion((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

  const guardar = async () => {
    try {
      await asignar.mutateAsync(seleccion)
      toast.success('Radiólogos asignados')
    } catch (err) {
      toast.error(getErrorMessage(err, 'No se pudieron asignar los radiólogos'))
    }
  }

  if (cargando) {
    return (
      <div className="flex items-center gap-2 text-sm text-[var(--foreground-muted)]">
        <Loader2 className="h-4 w-4 animate-spin" />
        Cargando radiólogos...
      </div>
    )
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-[var(--primary)]" />
        <h4 className="text-sm font-medium">Radiólogos que transcribe</h4>
      </div>

      <p className="text-xs text-[var(--foreground-muted)]">
        Su cola de trabajo tendrá los informes de los radiólogos que marque aquí.
        Si no marca ninguno, su pantalla estará vacía.
      </p>

      {radiologos.length === 0 ? (
        <div className="flex items-start gap-2 rounded-lg border border-[var(--border)] p-3 text-sm">
          <AlertCircle className="mt-0.5 h-4 w-4 text-[var(--color-warning)]" />
          <span>
            Esta institución todavía no tiene radiólogos activos. Créelos primero y vuelva aquí.
          </span>
        </div>
      ) : (
        <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-[var(--border)] p-2">
          {radiologos.map((r) => (
            <label
              key={r.id}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-[var(--background-subtle)]"
            >
              <input
                type="checkbox"
                checked={seleccion.includes(r.id)}
                onChange={() => alternar(r.id)}
              />
              <span>
                {[r.firstName, r.lastName].filter(Boolean).join(' ') || r.email}
              </span>
              <span className="ml-auto text-xs text-[var(--foreground-muted)]">{r.email}</span>
            </label>
          ))}
        </div>
      )}

      {radiologos.length > 0 && (
        <div className="flex items-center gap-3">
          <Button
            type="button"
            size="sm"
            onClick={guardar}
            disabled={!haCambiado || asignar.isPending}
          >
            {asignar.isPending ? 'Guardando...' : 'Guardar asignación'}
          </Button>
          <button
            type="button"
            className="text-xs text-[var(--primary)] hover:underline"
            onClick={() => setSeleccion(radiologos.map((r) => r.id))}
          >
            Marcar todos
          </button>
        </div>
      )}
    </div>
  )
}
